import { useState } from 'react';
import { askActions, useAsk } from '@/lib/askStore';
import type { DrcChatSummary } from '@/lib/drc';
import { EditIcon, FolderIcon, PlusIcon, StarIcon, TrashIcon } from './Icons';
import { SheetOption, useOverlay } from './Overlay';

/**
 * A chat's menu, as Claude's (Gary, 18 Oct): Star, Rename, Move to project, Delete. Used by the list row and
 * the open chat's title. The sheet renders outside the screen, so it reads the shared Ask store itself.
 */
export function AskChatMenu({ chat, onRename, onDeleted }: { chat: DrcChatSummary; onRename?: () => void; onDeleted: () => void }) {
  const { openSheet, closeSheet, toast } = useOverlay();
  const starred = !!chat.starred_at;
  return (
    <>
      <h2 className="chats-sheet-title">{chat.title || 'Untitled chat'}</h2>
      <SheetOption icon={<StarIcon filled={!starred} />} title={starred ? 'Unstar' : 'Star'} sub={starred ? 'Takes it out of Starred' : 'Keeps it in Starred at the top'}
        onClick={() => { closeSheet(); void askActions.star(chat.id, !starred).then((ok) => toast(ok ? (starred ? 'Unstarred' : 'Starred') : 'That did not save. Try again.')); }} />
      <SheetOption icon={<EditIcon />} title="Rename" sub="Give this chat a name you will recognise"
        onClick={() => { if (onRename) { closeSheet(); onRename(); } else openSheet(<RenameChat chat={chat} />); }} />
      <SheetOption icon={<FolderIcon />} title="Move to project" sub="Keep related chats together" onClick={() => openSheet(<MoveToProject chat={chat} />)} />
      <SheetOption icon={<TrashIcon />} title="Delete" sub="It goes from Sentinel too" danger onClick={() => openSheet(
        <>
          <h2 className="chats-sheet-title">Delete this chat?</h2>
          <SheetOption icon={<TrashIcon />} title="Delete chat" sub={`${chat.title || 'Untitled chat'}. It goes from Sentinel too.`} danger
            onClick={() => { closeSheet(); void askActions.remove(chat.id).then((ok) => { toast(ok ? 'Chat deleted' : 'Could not delete the chat'); if (ok) onDeleted(); }); }} />
        </>,
      )} />
    </>
  );
}

/** Renaming in a sheet, where the list row is not on screen (an open chat on a phone). */
function RenameChat({ chat }: { chat: DrcChatSummary }) {
  const { closeSheet, toast } = useOverlay();
  const [title, setTitle] = useState(chat.title ?? '');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!title.trim()) return; closeSheet(); void askActions.rename(chat.id, title.trim()).then((ok) => toast(ok ? 'Renamed' : 'That did not save. Try again.')); }}>
      <h2 className="chats-sheet-title">Rename chat</h2>
      <input className="chats-input" value={title} maxLength={200} autoFocus aria-label="Chat name" onChange={(e) => setTitle(e.target.value)} />
      <button type="submit" className="chats-btn chats-ask-rename-save" disabled={!title.trim()}>Save</button>
    </form>
  );
}

function MoveToProject({ chat }: { chat: DrcChatSummary }) {
  const { projects } = useAsk();
  const { closeSheet, toast } = useOverlay();
  const [name, setName] = useState('');
  const move = (id: string | null, label: string) => { closeSheet(); void askActions.move(chat.id, id).then((ok) => toast(ok ? label : 'That did not save. Try again.')); };
  return (
    <>
      <h2 className="chats-sheet-title">Move to project</h2>
      {projects.map((p) => (
        <SheetOption key={p.id} icon={<FolderIcon />} title={p.name} sub={p.id === chat.project_id ? 'In this project now' : ''} onClick={() => move(p.id, `Moved to ${p.name}`)} />
      ))}
      {chat.project_id && <SheetOption icon={<FolderIcon />} title="No project" sub="Back to Recents" onClick={() => move(null, 'Back in Recents')} />}
      <form className="chats-ask-newproject" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        void askActions.createProject(name).then((p) => (p ? move(p.id, `Moved to ${p.name}`) : toast('Could not create the project')));
      }}>
        <PlusIcon />
        <input className="chats-input" placeholder="New project name" aria-label="New project name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="chats-select-all" disabled={!name.trim()}>Create</button>
      </form>
    </>
  );
}

/** A project's menu: Rename, Delete (its chats go back to Recents). */
export function AskProjectMenu({ id, name, onRename, onNewChat }: { id: string; name: string; onRename: () => void; onNewChat?: () => void }) {
  const { closeSheet, toast } = useOverlay();
  return (
    <>
      <h2 className="chats-sheet-title">{name}</h2>
      {onNewChat && <SheetOption icon={<PlusIcon />} title="New chat in this project" sub="" onClick={() => { closeSheet(); onNewChat(); }} />}
      <SheetOption icon={<EditIcon />} title="Rename project" sub="" onClick={() => { closeSheet(); onRename(); }} />
      <SheetOption icon={<TrashIcon />} title="Delete project" sub="Its chats go back to Recents" danger
        onClick={() => { closeSheet(); void askActions.removeProject(id).then((ok) => toast(ok ? 'Project deleted' : 'Could not delete the project')); }} />
    </>
  );
}
